import { HttpClient, HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';

import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { IUserWithRoute, LoginModel } from '../models/legacy';
import { ChangePasswordModel } from '../models/recovery/change-password.model';
import { User } from '../models/vet-users/user.model';

import { MemberAccess } from '../models/common/member-access.model';
import { SpinnerService } from '../shared-new/services/spinner.service';
import { EncryptionService } from './encryption.service';
import { LocalStorageService } from './local-storage.service';
import { SalesSignalrService } from './sales-signalr.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public baseUrl = environment.apiAuthUrl;
  public portalUrl = environment.portalUrl;
  public repoUrl = environment.api2BaseUrl;

  public helper: { isTokenExpired(arg0: string): unknown };

  public onError: BehaviorSubject<string> = new BehaviorSubject('');
  public onAuthChange: BehaviorSubject<boolean> = new BehaviorSubject(null);

  public user: LoginModel = null;

  public serviceParameter = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
    Authorization: '',
  };

  public isVerifyCatalogHorse$ = new EventEmitter<boolean>();
  public isSalesCompanyUploadCsvHorses$ = new EventEmitter<boolean>();
  public isRequiredRegister$ = new EventEmitter();

  public constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly encService: EncryptionService,
    private spinnerService: SpinnerService,
    private readonly salesSignalRService: SalesSignalrService
  ) {
    this.helper = new JwtHelperService();

    const token = this.getToken();
    if (token) {
      this.serviceParameter.Authorization = `Bearer ${token}`;
    } else {
      delete this.serviceParameter.Authorization;
    }
  }

  private toggleLoginPopupVisibility = new BehaviorSubject<boolean>(false);
  private userImageSubject = new BehaviorSubject<string>(this.getUserImage());
  private userDataSubject = new BehaviorSubject<LoginModel>(this.getUserTokenResponse());
  userImage$ = this.userImageSubject.asObservable();
  userData$ = this.userDataSubject.asObservable();
  getLoginPopupVisibility(): Observable<boolean> {
    return this.toggleLoginPopupVisibility.asObservable();
  }

  setLoginPopupVisibility(newValue: boolean) {
    this.toggleLoginPopupVisibility.next(newValue);
    //console.log(newValue);
  }

  public login(userName: string, password: string): Observable<LoginModel> {
    this.spinnerService.addRequest('login'); // Show spinner
    const user = {
      username: userName,
      encPassword: this.encService.encryptAesWithIVY(password),
      clientInfo: {
        versionName: '1',
        apiVersionSupported: '2',
        devicePlatform: 'web',
        deviceIdiom: '',
        deviceVersion: '',
        deviceModel: 'TW Repo Website',
      },
    };

    return this.http
      .post<LoginModel>(`${this.baseUrl}/repository/auth`, user)
      .pipe(
        map((_user) => {
          this.user = _user;
          this.setToken(_user);
          return _user;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('login')) // Hide spinner on completion
      );
  }
  public anonymousLogin(encryptedKey: string): Observable<any> {
    this.spinnerService.addRequest('anonymous'); // Show spinner

    return this.http
      .post<LoginModel>(`${this.baseUrl}/repository/anonymousauth`, {
        authKey: encryptedKey,
      })
      .pipe(
        map((_user) => {
          this.user = _user;
          // this.setToken(_user);
          return _user;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('anonymous')) // Hide spinner on completion
      );
  }

  public getUserRouteInfoWithKey(
    userRouteKey: string
  ): Observable<HttpResponse<IUserWithRoute>> {
    this.spinnerService.addRequest('getUserRouteInfoWithKey'); // Show spinner
    return this.http
      .get<IUserWithRoute>(
        `${this.baseUrl}/auth/authWithEncryptedKey/?key=${encodeURIComponent(
          userRouteKey
        )}`,
        {
          withCredentials: false,
          observe: 'response',
        }
      )
      .pipe(
        map((userWithRoute) => {
          this.user = userWithRoute.body.userLogin;
          this.setToken(this.user);
          return userWithRoute;
        }),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() =>
          this.spinnerService.popRequest('getUserRouteInfoWithKey')
        ) // Hide spinner on completion
      );
  }

  public setToken(user: LoginModel): void {
    this.localStorageService.setItem('User', JSON.stringify(user));
    this.userDataSubject.next(user);
  }

  /**
   * Updates only the user display information (firstName, lastName, email)
   * without affecting the authentication token. This method merges the new
   * display data with the existing user object to preserve all other fields.
   * @param firstName - The updated first name
   * @param lastName - The updated last name
   * @param email - The updated email address
   */
  public updateUserDisplayInfo(firstName: string, lastName: string, email: string): void {
    const currentUser = this.getUserTokenResponse();
    if (currentUser && firstName && lastName) {
      const updatedUser: LoginModel = {
        ...currentUser,
        firstName,
        lastName,
        emailAddress: email || currentUser.emailAddress
      };
      this.localStorageService.setItem('User', JSON.stringify(updatedUser));
      this.userDataSubject.next(updatedUser);
    }
  }

  public setUserImage(image: string): void {
    this.localStorageService.setItem('profile', JSON.stringify(image));
    this.userImageSubject.next(image);
  }
  public setUserProfileStatus(hasProflie: boolean): void {
    this.localStorageService.setItem('profileStatus', hasProflie ? '1' : '-1');
  }
  public hasUserProfile(): boolean {
    const status = this.localStorageService.getItem('profileStatus');
    if (status) {
      return status == '1' ? true : false;
    }
    return false;
  }
  public getUserImage(): string {
    const img = this.localStorageService.getItem('profile');
    if (img) {      
      return JSON.parse(img);
    }
    return 'assets/images/user.png';
  }

  public getUserTokenResponse(): LoginModel {
    const localUser = this.localStorageService.getItem('User');
    if (localUser) {
      const user = JSON.parse(this.localStorageService.getItem('User'));
      return user;
    }
    return null;
  }

  public getToken(): string {
    const localUser = this.localStorageService.getItem('User');
    if (localUser) {
      const user = JSON.parse(this.localStorageService.getItem('User'));
      return user ? user.accessToken : null;
    }
    return null;
  }

  public getUserType(): string {
    const localUser = this.localStorageService.getItem('User');
    if (localUser) {
      const user = JSON.parse(this.localStorageService.getItem('User'));
      return user.userType;
    }
    return '';
  }

  public IsSalesCompany(): boolean {
    const type = this.getUserType();
    if (type === 'SalesCompany') {
      return true;
    }
    return false;
  }

  public IsSuperUser(): boolean {
    const type = this.getUserType();
    if (type === 'Default') {
      return true;
    }
    return false;
  }

  public IsVet(): boolean {
    const type = this.getUserType();
    if (type === 'PublicVet') {
      return true;
    }
    return false;
  }

  public getUserData(): Observable<string> {
    return this.localStorageService.localStorage$.pipe(
      map((data: { [key: string]: any }) => {
        return this.localStorageService.getItem('User');
      })
    );
  }

  public getMemberLotOrHipText(): Observable<string> {
    return this.localStorageService.localStorage$.pipe(
      map((data: { [key: string]: any }) => {
        const userDataJson = this.localStorageService.getItem('User');
        if (
          userDataJson === null ||
          typeof userDataJson === 'undefined' ||
          userDataJson === ''
        ) {
          return 'HIP';
        }
        const user = JSON.parse(userDataJson);
        const isUSA: boolean = user?.region?.toLowerCase() === 'usa';
        return isUSA ? 'HIP' : 'LOT';
      })
    );
  }
  public IsMemberFromUSA(): Observable<boolean> {
    return this.localStorageService.localStorage$.pipe(
      map((data: { [key: string]: any }) => {
        const userDataJson = this.localStorageService.getItem('User');
        if (
          userDataJson === null ||
          typeof userDataJson === 'undefined' ||
          userDataJson === ''
        ) {
          return true;
        }
        const user = JSON.parse(userDataJson);
        const isUSA: boolean = user?.region?.toLowerCase() === 'usa';
        return isUSA ? true : false;
      })
    );
  }

  public logout(): Observable<boolean> {
    this.spinnerService.addRequest('logout');
    return this.http
      .post<boolean>(`${this.baseUrl}/Logout`, null, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => {
          this.clearLocalStorage();
          return res.body;
        }),
        catchError((error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('logout')) // Hide spinner on completion
      );
  }
  public clearLocalStorage(): void {
    this.salesSignalRService.forceCloseConnection();
    this.localStorageService.clear();
  }

  public isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    let isAuthenticated = false;
    if (token) {
      if (!this.helper.isTokenExpired(token)) {
        this.onAuthChange.next(true);
        isAuthenticated = true;
      } else {
        this.onAuthChange.next(false);
        isAuthenticated = false;
      }
    } else {
      this.onAuthChange.next(false);
      isAuthenticated = false;
    }
    return isAuthenticated && user.userId > 0;
  }

  public forgotPasswordByUserName(
    username: string
  ): Observable<HttpResponse<boolean>> {
    this.spinnerService.addRequest('forgotPasswordByUserName'); // Show spinner
    return this.http
      .get<boolean>(`${this.baseUrl}/${username}/ResetPassword/username`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() =>
          this.spinnerService.popRequest('forgotPasswordByUserName')
        ) // Hide spinner on completion
      );
  }

  public getUserByRecoveryEncryptedId(encryptedId: string): Observable<User> {
    this.spinnerService.addRequest('getUserByRecoveryEncryptedId'); // Show spinner
    return this.http
      .get<User>(`${this.baseUrl}/${encryptedId}/userInfoWithRecKey`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() =>
          this.spinnerService.popRequest('getUserByRecoveryEncryptedId')
        ) // Hide spinner on completion
      );
  }

  public forgotPasswordByUserId(
    userId: number
  ): Observable<HttpResponse<boolean>> {
    this.spinnerService.addRequest('forgotPasswordByUserId'); // Show spinner
    return this.http
      .get<boolean>(`${this.baseUrl}/${userId}/ResetPassword/userid`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('forgotPasswordByUserId')) // Hide spinner on completion
      );
  }

  public updatePassword(request: ChangePasswordModel): Observable<boolean> {
    this.spinnerService.addRequest('updatePassword'); // Show spinner
    return this.http
      .post<boolean>(`${this.baseUrl}/changePasswordWithKey`, request, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('updatePassword')) // Hide spinner on completion
      );
  }

  public changePassword(formData: any): Observable<{}> {
    this.spinnerService.addRequest('changePassword'); // Show spinner
    return this.http
      .post(`${this.baseUrl}/changePassword`, formData, {
        withCredentials: false,
        observe: 'response',
        headers: this.serviceParameter,
      })
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('changePassword')) // Hide spinner on completion
      );
  }

  public getMemberAccessData(
    saleId?: number,
    horseId?: number
  ): Observable<MemberAccess> {
    this.spinnerService.addRequest('getMemberAccessData'); // Show spinner
    let noSaleId = saleId === null || typeof saleId === 'undefined';
    let noHorseId = horseId === null || typeof horseId === 'undefined';
    let query = noSaleId ? '' : `?saleId=${saleId}`;

    if (!noSaleId && !noHorseId) {
      query += `&horseId=${horseId}`;
    }

    return this.http
      .get<MemberAccess>(`${this.repoUrl}/api/Member/memberAccess${query}`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getMemberAccessData')) // Hide spinner on completion
      );
  }

  public set GlobalMemberAccessData(memberAccessData: MemberAccess) {
    this.localStorageService.GlobalMemberAccessData = memberAccessData;
  }

  public get GlobalMemberAccessData() {
    return this.localStorageService.GlobalMemberAccessData;
  }

  public getSaleMemberAccessDataAsObservable(
    saleId: number,
    horseId: number
  ): Observable<MemberAccess> {
    return from(this.getSaleMemberAccessData(saleId, horseId));
  }

  public getSaleMemberAccessData(
    saleId: number,
    horseId: number
  ): Promise<MemberAccess> {
    this.spinnerService.addRequest('getSaleMemberAccessData'); // Show spinner
    return new Promise(async (resolve, reject) => {
      let saleAccessData: MemberAccess;
      this.getMemberAccessData(saleId, horseId)
        .pipe(take(1))
        .subscribe((data) => {
          saleAccessData = data as unknown as MemberAccess;
          this.localStorageService.setSaleMemberAccessData(
            saleId,
            saleAccessData
          );
          this.spinnerService.popRequest('getSaleMemberAccessData');
          resolve(saleAccessData);
        });
    });
  }

  public getUser(): User {
    return this.localStorageService.GetUser;
  }

  public refreshTwRepoData(): Observable<{}> {
    this.spinnerService.addRequest('refreshTwRepoData'); // Show spinner
    return this.http
      .post(`${this.baseUrl}/repository/RefreshTwRepoData`, {
        withCredentials: false,
        observe: 'response',
        headers: this.serviceParameter,
      })
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('refreshTwRepoData')) // Hide spinner on completion
      );
  }

  refreshTwSaleHorseDataWithDelete(
    saleId: number,
    needToDeleteSaleHorses: boolean
  ): Observable<any> {

    if (!needToDeleteSaleHorses) {
      // Skip delete -> refresh only
      return this.refreshTwSaleHorseData(saleId);
    }
    this.spinnerService.addRequest('refreshTwSaleHorseData'); // Show spinner
    // Step 1: call delete
    return this.http
      .delete(`${this.repoUrl}/api/Sales/${saleId}/delete/salehorses`)
      .pipe(
        switchMap((response: any) => {
          // Check delete success message
          if (response === "Successfully deleted SaleHorse") {
            // Step 2: call refresh only AFTER delete success
            return this.refreshTwSaleHorseData(saleId);
          }

          // If delete didn’t happen -> still refresh
          return this.refreshTwSaleHorseData(saleId);
        }),
        catchError((error) => {
          this.spinnerService.popRequest('refreshTwSaleHorseData')
          this.onError.next(error.message);
          return throwError(error);
        })
      );
  }


  public refreshTwSaleHorseData(saleId:number): Observable<{}> {
    this.spinnerService.addRequest('refreshTwSaleHorseData'); // Show spinner
    return this.http
      .post(`${this.baseUrl}/repository/${saleId}/RefreshTwSaleHorseData`, {
        withCredentials: false,
        observe: 'response',
        headers: this.serviceParameter,
      })
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('refreshTwSaleHorseData')) // Hide spinner on completion
      );
  }
}
